import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/movies' },
  { label: '资讯', to: '/news' },
  { label: '专题', to: '/specials' },
  { label: '关于', to: '/about' },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [genresOpen, setGenresOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const isHome = location.pathname === '/';
  const solid = scrolled || !isHome || menuOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setGenresOpen(false);
    setKeyword('');
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) {
      navigate('/search');
      return;
    }
    navigate(`/search?q=${encodeURIComponent(value)}`);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid
          ? 'border-b border-background-300/50 bg-background-50/90 backdrop-blur-xl'
          : 'border-b border-transparent bg-gradient-to-b from-background-50/80 to-transparent'
      }`}
    >
      <div className="shell flex h-16 items-center justify-between gap-4 md:h-20">
        <Link to="/" className="flex items-center gap-2.5 md:gap-3" aria-label="午夜电影网首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50 md:h-10 md:w-10">
            <i className="ri-play-fill text-xl md:text-2xl" aria-hidden="true"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-[0.14em] text-foreground-950 md:text-xl">午夜电影网</span>
            <span className="eyebrow mt-1 text-[9px] text-primary-500 md:text-[10px]">Midnight Cinema</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm transition-colors ${
                  isActive ? 'text-primary-500' : 'text-foreground-700 hover:text-foreground-950'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  <span
                    className={`absolute inset-x-4 -bottom-0.5 h-0.5 rounded-full bg-primary-500 transition-opacity ${
                      isActive ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <form onSubmit={submitSearch} className="hidden items-center md:flex" role="search">
            <div className="flex h-9 items-center gap-2 rounded-full border border-background-300/70 bg-background-100/80 px-3 transition-colors focus-within:border-primary-500/70">
              <i className="ri-search-line text-sm text-foreground-500" aria-hidden="true"></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索电影、导演、资讯"
                aria-label="搜索电影或资讯"
                className="w-32 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none xl:w-48"
              />
            </div>
          </form>

          <Link
            to="/movies"
            className="hidden items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:flex"
          >
            <i className="ri-film-line text-base" aria-hidden="true"></i>
            浏览片库
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label={menuOpen ? '关闭菜单' : '打开菜单'}
            aria-expanded={menuOpen}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300/70 text-foreground-800 transition-colors hover:border-primary-500/70 hover:text-primary-500 lg:hidden"
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-3-line text-xl'} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="fixed inset-x-0 top-16 bottom-0 z-40 overflow-y-auto border-t border-background-300/40 bg-background-50 px-4 pb-10 pt-5 lg:hidden md:top-20">
          <form onSubmit={submitSearch} className="mb-5" role="search">
            <div className="flex h-11 w-full items-center gap-2 rounded-lg border border-background-300/70 bg-background-100 px-3 focus-within:border-primary-500/70">
              <i className="ri-search-line text-base text-foreground-500" aria-hidden="true"></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索电影、导演、资讯"
                aria-label="搜索电影或资讯"
                className="h-full w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
              <button type="submit" className="whitespace-nowrap text-sm text-primary-500">
                搜索
              </button>
            </div>
          </form>

          <nav className="flex flex-col" aria-label="移动端导航">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `flex items-center justify-between border-b border-background-200/80 py-4 text-base ${
                    isActive ? 'text-primary-500' : 'text-foreground-800'
                  }`
                }
              >
                <span>{item.label}</span>
                <i className="ri-arrow-right-s-line text-lg text-foreground-500" aria-hidden="true"></i>
              </NavLink>
            ))}
          </nav>

          <div className="mt-6">
            <button
              type="button"
              onClick={() => setGenresOpen((prev) => !prev)}
              aria-expanded={genresOpen}
              className="flex w-full items-center justify-between border-b border-background-200/80 py-3 text-sm text-foreground-700"
            >
              <span className="flex items-center gap-2">
                <i className="ri-price-tag-3-line text-base text-primary-500" aria-hidden="true"></i>
                热门类型
              </span>
              <i className={genresOpen ? 'ri-arrow-up-s-line text-lg text-foreground-500' : 'ri-arrow-down-s-line text-lg text-foreground-500'} aria-hidden="true"></i>
            </button>
            {genresOpen && (
              <div className="mt-3 flex flex-wrap gap-2">
                {['动作', '科幻', '爱情', '喜剧', '剧情', '悬疑', '犯罪', '家庭', '武侠'].map((genre) => (
                  <Link
                    key={genre}
                    to={`/movies?genre=${encodeURIComponent(genre)}`}
                    className="whitespace-nowrap rounded-full border border-background-300/70 px-3 py-1.5 text-sm text-foreground-700"
                  >
                    {genre}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/movies"
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-lg bg-primary-500 py-3 text-sm font-medium text-background-50"
          >
            <i className="ri-play-list-2-line text-base" aria-hidden="true"></i>
            浏览全部电影
          </Link>
        </div>
      )}
    </header>
  );
}